import React, {useEffect, useState} from 'react';

import Logo from '../assets/img/logo.svg';
import {useIntl} from '../services/IntlContext';

// Le préchargeur couvre deux moments, et deux seulement :
//   1. le démarrage de l'application, le temps que le document finisse de charger (feuilles de
//      style, polices, scripts Leaflet du <head>) ;
//   2. l'entrée dans la plateforme après une connexion valide (replayAppLoading, Signin.js).
// Entre les deux, les navigations sont purement côté client et instantanées — chaque page gère
// elle-même le chargement de ses données, avec ses propres indicateurs.
//
// Le drapeau vit donc au niveau du module et non dans un état React : <Switch> démonte/remonte
// un wrapper de route à chaque navigation, un état local repartirait à zéro et rejouerait un
// écran plein cadre à chaque clic dans le menu.
let appLoaded = false;

// Plancher d'affichage au démarrage : le préchargeur reste à l'écran au moins ce temps-là même
// si le document est déjà prêt — sans quoi il ne serait quasiment jamais visible, le chargement
// étant très rapide. Passer à 0 pour ne l'afficher que s'il y a réellement à attendre.
const MIN_VISIBLE_MS = 500;

// Écran de transition après une connexion réussie (voir replayAppLoading). Constante distincte
// du plancher de démarrage : les deux se règlent indépendamment.
const SIGNIN_HOLD_MS = 500;

const FADE_MS = 260; // doit rester aligné sur la transition de .ccv2-preloader

// Début et durée de la phase de chargement en cours (démarrage, puis chaque connexion).
let loadingSince = Date.now();
let holdMs = MIN_VISIBLE_MS;

/**
 * Rejoue l'écran de chargement. Appelé après une connexion valide, juste avant la redirection :
 * <Switch> monte alors le wrapper du tableau de bord, dont le useAppLoading lit ce drapeau au
 * montage et affiche l'écran le temps que la plateforme se mette en place.
 */
export const replayAppLoading = (durationMs = SIGNIN_HOLD_MS) => {
  appLoaded = false;
  loadingSince = Date.now();
  holdMs = durationMs;
};

/**
 * Indique si l'application est encore en phase de chargement : document pas encore chargé
 * (signal réel `window.load`, pas un minuteur) ou durée d'affichage pas encore écoulée.
 * Repasse à `false` une fois la phase terminée — une navigation ne le rejoue pas, seul
 * replayAppLoading le fait.
 */
export const useAppLoading = () => {
  const [loading, setLoading] = useState(!appLoaded);

  useEffect(() => {
    if (appLoaded) {
      return;
    }
    let unmounted = false;

    const finish = () => {
      const remaining = Math.max(0, holdMs - (Date.now() - loadingSince));
      // Volontairement non annulé au démontage : la phase de chargement est globale à
      // l'application, pas propre à la route qui se trouvait montée à ce moment-là.
      setTimeout(() => {
        appLoaded = true;
        if (!unmounted) {
          setLoading(false);
        }
      }, remaining);
    };

    // `load` a pu se déclencher entre le rendu et cet effet.
    if (document.readyState === 'complete') {
      finish();
    } else {
      window.addEventListener('load', finish);
    }

    return () => {
      unmounted = true;
      window.removeEventListener('load', finish);
    };
  }, []);

  return loading;
};

export default (props) => {
  const {show} = props;
  const {intl} = useIntl();

  // Le fondu de sortie doit pouvoir se jouer : on garde le nœud monté le temps de la
  // transition, puis on le retire pour ne pas laisser traîner un calque plein écran en
  // z-index 9999 au-dessus de l'application.
  const [mounted, setMounted] = useState(show);

  useEffect(() => {
    if (show) {
      setMounted(true);
      return;
    }
    const timer = setTimeout(() => setMounted(false), FADE_MS);
    return () => clearTimeout(timer);
  }, [show]);

  if (!mounted) {
    return null;
  }

  return (
    <div className={`ccv2-preloader ${show ? '' : 'is-leaving'}`} role="status" aria-live="polite">
      <span className="ccv2-preloader-logo">
        <img src={Logo} alt="" aria-hidden="true"/>
      </span>
      <span className="ccv2-preloader-bar"><i/></span>
      <span className="visually-hidden">{intl.formatMessage({id: 'LOADING'})}</span>
    </div>
  );
};
